import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ChatService } from '../services/chatService';
import {
  ChatMessage,
  ChatSession,
  ChatSessionStorage,
  ChatRequest,
  CHAT_PRESETS,
} from '../models/chat';

const Chat: React.FC = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [systemPrompt, setSystemPrompt] = useState(CHAT_PRESETS[0].systemPrompt);
  const [selectedPreset, setSelectedPreset] = useState(CHAT_PRESETS[0].name);
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const inputRef = useRef<null | HTMLInputElement>(null);

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('chatSessions');
    if (stored) {
      const data: ChatSessionStorage = JSON.parse(stored);
      const loadedSessions = data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt),
        lastUpdated: new Date(s.lastUpdated),
        messages: s.messages.map(m => ({
          ...m,
          timestamp: new Date(m.timestamp)
        }))
      }));
      setSessions(loadedSessions);
      
      // Set the most recent session as current
      if (loadedSessions.length > 0 && !currentSessionId) {
        setCurrentSessionId(loadedSessions[0].id);
      }
    }
  }, []);

  // Save sessions to localStorage
  const saveSessions = (newSessions: ChatSession[]) => {
    const storage: ChatSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('chatSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessions, currentSessionId]);

  const getCurrentSession = (): ChatSession | undefined => {
    return sessions.find(s => s.id === currentSessionId);
  };

  const createNewSession = () => {
    const newSession: ChatSession = {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      lastUpdated: new Date()
    };
    saveSessions([newSession, ...sessions]);
    setCurrentSessionId(newSession.id);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || loading) return;

    let currentSession = getCurrentSession();
    if (!currentSession) {
      createNewSession();
      currentSession = sessions[0];
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message.trim(),
      timestamp: new Date()
    };

    // Update session with user message
    const updatedSession = {
      ...currentSession,
      messages: [...currentSession.messages, userMessage],
      lastUpdated: new Date()
    };

    // Update title if it's the first message
    if (updatedSession.messages.length === 1) {
      updatedSession.title = message.trim().substring(0, 50) + (message.trim().length > 50 ? '...' : '');
    }

    const updatedSessions = sessions.map(s => 
      s.id === currentSession!.id ? updatedSession : s
    );
    saveSessions(updatedSessions);

    setMessage('');
    setLoading(true);
    setError(null);

    try {
      const request: ChatRequest = {
        message: userMessage.content,
        sessionId: currentSession.id,
        maxTokens,
        temperature,
        systemPrompt
      };

      const response = await ChatService.sendMessage(request);
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date()
      };

      // Update session with assistant response
      const finalSession = {
        ...updatedSession,
        messages: [...updatedSession.messages, assistantMessage],
        lastUpdated: new Date()
      };

      const finalSessions = sessions.map(s => 
        s.id === currentSession!.id ? finalSession : s
      );
      saveSessions(finalSessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const handleExportSession = (session: ChatSession) => {
    let content = `# Chat Session: ${session.title}\n\n`;
    content += `Date: ${session.createdAt.toLocaleDateString()}\n\n`;
    
    session.messages.forEach((msg) => {
      content += `## ${msg.role === 'user' ? 'User' : 'Assistant'}\n`;
      content += `*${msg.timestamp.toLocaleString()}*\n\n`;
      content += `${msg.content}\n\n---\n\n`;
    });

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${session.title.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteSession = (sessionId: string) => {
    const newSessions = sessions.filter(s => s.id !== sessionId);
    saveSessions(newSessions);
    
    if (currentSessionId === sessionId) {
      setCurrentSessionId(newSessions.length > 0 ? newSessions[0].id : null);
    }
  };

  const handleClearChat = () => {
    const currentSession = getCurrentSession();
    if (currentSession) {
      const updatedSession = {
        ...currentSession,
        messages: [],
        lastUpdated: new Date()
      };
      const updatedSessions = sessions.map(s => 
        s.id === currentSession.id ? updatedSession : s
      );
      saveSessions(updatedSessions);
    }
  };

  const handlePresetChange = (presetName: string) => {
    setSelectedPreset(presetName);
    const preset = CHAT_PRESETS.find(p => p.name === presetName);
    if (preset) {
      setSystemPrompt(preset.systemPrompt);
      setTemperature(preset.temperature);
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', mt: 3 }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BotIcon sx={{ fontSize: 30, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">
                AI Chat Assistant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getCurrentSession()?.title || 'Start a new conversation'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Tooltip title="New Chat">
              <IconButton onClick={createNewSession}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sessions">
              <IconButton onClick={(e) => setSessionMenuAnchor(e.currentTarget)}>
                <MoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <IconButton onClick={() => setSettingsOpen(true)}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {getCurrentSession()?.messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 8 }}>
              <BotIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Welcome to AI Chat
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start a conversation by typing a message below
              </Typography>
              <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {['How can you help me?', 'Tell me a joke', 'Explain quantum computing', 'Write a poem'].map((prompt) => (
                  <Chip
                    key={prompt}
                    label={prompt}
                    onClick={() => setMessage(prompt)}
                    clickable
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <List>
              {getCurrentSession()?.messages.map((msg) => (
                <ListItem
                  key={msg.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    py: 1
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main' }}>
                      {msg.role === 'user' ? <PersonIcon /> : <BotIcon />}
                    </Avatar>
                    <Typography variant="subtitle2">
                      {msg.role === 'user' ? 'You' : 'Assistant'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {msg.timestamp.toLocaleTimeString()}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyMessage(msg.content)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Card 
                    sx={{ 
                      maxWidth: '80%', 
                      bgcolor: msg.role === 'user' ? 'primary.light' : 'background.paper',
                      ml: 4
                    }}
                  >
                    <CardContent>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
              <div ref={messagesEndRef} />
            </List>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={30} />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              inputRef={inputRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {message && (
                      <IconButton size="small" onClick={() => setMessage('')}>
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!message.trim() || loading}
              sx={{ minWidth: 100 }}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Sessions Menu */}
      <Menu
        anchorEl={sessionMenuAnchor}
        open={Boolean(sessionMenuAnchor)}
        onClose={() => setSessionMenuAnchor(null)}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Chat Sessions</Typography>
        </MenuItem>
        <Divider />
        {sessions.map((session) => (
          <MenuItem
            key={session.id}
            onClick={() => {
              setCurrentSessionId(session.id);
              setSessionMenuAnchor(null);
            }}
            selected={session.id === currentSessionId}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box>
                <Typography variant="body2">{session.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {session.messages.length} messages • {session.lastUpdated.toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportSession(session);
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSession(session.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </MenuItem>
        ))}
        {sessions.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No sessions yet
            </Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleClearChat}>
          <ClearIcon sx={{ mr: 1 }} />
          Clear Current Chat
        </MenuItem>
      </Menu>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chat Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Preset</InputLabel>
              <Select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                label="Preset"
              >
                {CHAT_PRESETS.map((preset) => (
                  <MenuItem key={preset.name} value={preset.name}>
                    {preset.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="System Prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              helperText="Instructions that guide the AI's behavior"
            />

            <Box>
              <Typography gutterBottom>Temperature: {temperature}</Typography>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: 'Precise' },
                  { value: 0.5, label: 'Balanced' },
                  { value: 1, label: 'Creative' }
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box>
              <Typography gutterBottom>Max Tokens: {maxTokens}</Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={512}
                max={4096}
                step={512}
                marks={[
                  { value: 512, label: '512' },
                  { value: 2048, label: '2048' },
                  { value: 4096, label: '4096' }
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Chat;